import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Mail, Lock, User, Github } from "lucide-react";
import { isNetworkError, retry } from "@/lib/networkUtils";
import AuthErrorBoundary from "./AuthErrorBoundary";
import FullNameInput from "./fields/FullNameInput";
import UsernameInput from "./fields/UsernameInput";
import EmailInput from "./fields/EmailInput";
import PasswordInput from "./fields/PasswordInput";
import SocialAuthButtons from "./SocialAuthButtons";
import AuthFooterLinks from "./AuthFooterLinks";
import EmailConfirmationPage from "./EmailConfirmationPage";

interface AuthFormProps {
  mode: 'login' | 'signup';
}

export const AuthForm = ({ mode }: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const navigate = useNavigate();

  // Real-time username check during sign up
  useEffect(() => {
    let active = true;
    if (mode === "signup" && username.length >= 3) {
      setCheckingUsername(true);

      (async () => {
        try {
          const { data } = await retry(async () => {
            return await supabase
              .from("profiles")
              .select("id")
              .eq("username", username)
              .maybeSingle();
          });
          if (active) setUsernameAvailable(!data);
        } catch (err: any) {
          if (isNetworkError(err)) {
            setFieldErrors((prev) => ({
              ...prev,
              username: "Network problem—check your connection and retry.",
            }));
          } else {
            setFieldErrors((prev) => ({
              ...prev,
              username: "Failed to check username availability.",
            }));
          }
          setUsernameAvailable(null);
        } finally {
          if (active) setCheckingUsername(false);
        }
      })();
    } else if (mode === "signup") {
      setUsernameAvailable(null);
    }
    return () => {
      active = false;
    };
  }, [mode, username]);

  const validateFields = () => {
    let errors: { [key: string]: string } = {};
    if (mode === "signup") {
      if (!fullName.trim()) errors.fullName = "Full name is required";
      if (!username.trim()) errors.username = "Username is required";
      else if (!/^[a-zA-Z0-9_-]{3,}$/.test(username))
        errors.username = "Alphanumeric, dashes/underscores, 3+ chars";
      else if (usernameAvailable === false)
        errors.username = "That username is already taken";
    }
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^@]+@[^.]+\..+$/.test(email))
      errors.email = "Must be a valid email";
    if (!password) errors.password = "Password is required";
    else if (mode === "signup" && password.length < 8)
      errors.password = "Min 8 characters";
    return errors;
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setLoading(true);

    try {
      if (mode === 'signup') {
        // Pre-check for username collision just before submit
        let existing = null;
        try {
          const { data } = await retry(async () =>
            await supabase
              .from('profiles')
              .select('id')
              .eq('username', username)
              .maybeSingle());
          existing = data;
        } catch (err: any) {
          if (isNetworkError(err)) throw new Error("Network issue. Please retry.");
          throw err;
        }
        if (existing) {
          setFieldErrors(prev => ({
            ...prev,
            username: "That username is already taken"
          }));
          setLoading(false);
          return;
        }

        const { error } = await retry(() => supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              full_name: fullName,
              username: username,
            }
          }
        }));

        if (error) {
          if (error.message?.includes("rate limit")) {
            setFieldErrors({ email: "Too many signups. Wait and try again." });
            return;
          }
          if (error.message?.toLowerCase().includes("network")) {
            setFieldErrors({ email: "Network error. Please check connection and try again." });
            return;
          }
          setFieldErrors({ email: error.message });
          return;
        }

        toast.success("Check your email for the confirmation link!");

        // Redirect to Email Confirmation page, passing email as query param
        window.location.href = `/auth/email-confirmation?email=${encodeURIComponent(email)}`;
        return;
      } else {
        const { error } = await retry(() => supabase.auth.signInWithPassword({ email, password }));
        if (error) {
          if (
            error.message?.includes("Invalid login credentials") ||
            error.message?.includes("Wrong email or password")
          ) {
            setFieldErrors({ email: "Invalid credentials", password: " " });
            return;
          }
          if (error.message?.toLowerCase().includes("network")) {
            setFieldErrors({ email: "Network error. Please check your connection and try again." });
            return;
          }
          if (error.message?.includes("rate limit")) {
            setFieldErrors({ email: "Too many attempts. Please wait and try again." });
            return;
          }
          setFieldErrors({ email: error.message });
          return;
        }

        toast.success("Welcome back!");
        navigate("/");
      }
    } catch (error: any) {
      if (isNetworkError(error)) {
        toast.error("Network error. Check your connection and try again.");
      } else {
        toast.error(error.message || "An error occurred during authentication");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      const { error } = await retry(() =>
        supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: `${window.location.origin}/` }
        }));
      if (error) {
        if (error.message?.includes("OAuth") || error.message?.includes("redirect")) {
          toast.error("Google sign-in failed: Misconfigured redirect or provider; check project setup.");
        } else if (isNetworkError(error)) {
          toast.error("Network error—check connection and try again.");
        } else {
          toast.error(error.message || "Failed to authenticate with Google");
        }
      }
    } catch (err: any) {
      if (isNetworkError(err)) {
        toast.error("Network error—check your connection and retry.");
      } else {
        toast.error("Failed to authenticate with Google");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGitHubAuth = async () => {
    setLoading(true);
    try {
      const { error } = await retry(() =>
        supabase.auth.signInWithOAuth({
          provider: 'github',
          options: { redirectTo: `${window.location.origin}/` }
        }));
      if (error) {
        if (error.message?.includes("OAuth") || error.message?.includes("redirect")) {
          toast.error("GitHub sign-in failed: Misconfigured redirect or provider; check project setup.");
        } else if (isNetworkError(error)) {
          toast.error("Network error—check connection and try again.");
        } else {
          toast.error(error.message || "Failed to authenticate with GitHub");
        }
      }
    } catch (err: any) {
      if (isNetworkError(err)) {
        toast.error("Network error—check your connection and retry.");
      } else {
        toast.error("Failed to authenticate with GitHub");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthErrorBoundary>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {mode === 'login' ? 'Welcome Back' : 'Join The Academy'}
          </CardTitle>
          <CardDescription className="text-center">
            {mode === 'login'
              ? 'Sign in to your account to continue your intellectual journey'
              : 'Create an account to begin your exploration of human knowledge'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Social Auth Buttons */}
          <SocialAuthButtons
            onGoogle={handleGoogleAuth}
            onGitHub={handleGitHubAuth}
            loading={loading}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-black">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {mode === 'signup' && (
              <>
                <FullNameInput
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={fieldErrors.fullName}
                />
                <UsernameInput
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  error={fieldErrors.username}
                  checking={checkingUsername}
                  available={usernameAvailable}
                />
              </>
            )}
            <EmailInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
            />
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              minLength={8}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            <Button type="submit" className="w-full" disabled={loading || checkingUsername}>
              {loading ? "Please wait..." : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {/* Footer Links */}
          <AuthFooterLinks mode={mode} />
        </CardContent>
      </Card>
    </AuthErrorBoundary>
  );
};

// NOTICE: This file is now over 250 lines long. 
// For further enhancements, consider refactoring this form and splitting out field-level components or username logic!
